import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import { ComponentPicker } from '../components/ComponentPicker';
import { listTypes } from '../components/api';
import { useLabel } from '../components/labels';
import { LocationPicker } from '../locations/LocationPicker';
import { LabelDesigner } from './designer/LabelDesigner';
import { PrintTabs } from './PrintTabs';
import {
  createTemplate,
  deleteTemplate,
  listTemplates,
  templateStock,
  updateTemplate,
  type LabelTemplate,
} from './api';

/**
 * The templates this workshop prints with: what they look like, what they fit,
 * and what they spend.
 *
 * Beside the queue rather than under Administration. The person who adjusts a
 * label is the person standing at the printer holding the roll it came off —
 * the same person the queue is for, and the same permission.
 */
export function TemplatesPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ['labels', 'templates', 'all'],
    queryFn: () => listTemplates(undefined, undefined, true),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['labels', 'templates'] });
  const fail = (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown');

  const copy = useMutation({
    mutationFn: (source: LabelTemplate) =>
      createTemplate({
        // Suffixed rather than prompted for: the code is an identifier nobody
        // reads twice, and asking for one before the copy exists is a question
        // in front of a one-press action.
        code: `${source.code}-copy-${Date.now().toString(36).slice(-4)}`,
        name_ru: `${source.name_ru} (копия)`,
        name_en: `${source.name_en} (copy)`,
        copy_of: source.id,
      }),
    onSuccess: (created) => {
      setError(null);
      refresh();
      setEditing(created.id);
    },
    onError: fail,
  });

  const drop = useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const rows = templates.data ?? [];
  const open = rows.find((row) => row.id === editing) ?? null;

  if (open) {
    return <LabelDesigner template={open} onClose={() => setEditing(null)} />;
  }

  return (
    <div className="templates">
      <PrintTabs />
      <div className="station__head">
        <h2>{t('labels.templates')}</h2>
        <span className="hint">{t('labels.templatesHint')}</span>
      </div>

      {error && (
        <p className="panel__error">{t(`labels.errors.${error}`, t('labels.errors.unknown'))}</p>
      )}

      <ul className="templates__list">
        {rows.map((template) => (
          <TemplateRow
            key={template.id}
            template={template}
            canEdit={can('label.print')}
            onDesign={() => setEditing(template.id)}
            onCopy={() => copy.mutate(template)}
            onDelete={() => {
              if (window.confirm(t('labels.deleteConfirm', { name: template.name_ru }))) {
                drop.mutate(template.id);
              }
            }}
          />
        ))}
      </ul>
      {rows.length === 0 && !templates.isLoading && (
        <p className="panel__hint">{t('labels.noTemplates')}</p>
      )}
    </div>
  );
}

function TemplateRow({
  template,
  canEdit,
  onDesign,
  onCopy,
  onDelete,
}: {
  template: LabelTemplate;
  canEdit: boolean;
  onDesign: () => void;
  onCopy: () => void;
  onDelete: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const name = i18n.language.startsWith('en') ? template.name_en : template.name_ru;

  return (
    <li className="templates__row">
      <div className="templates__main">
        <div>
          <strong>{name}</strong>
          <em className="hint">
            {' '}
            {template.media_width_mm}×{template.media_height_mm} {t('labels.mm')} ·{' '}
            {t(`labels.target.${template.target}`)}
          </em>
          {/* Said on the row, because it decides which of the two buttons below
              does anything: a shipped layout is copied, not edited. */}
          {template.is_seeded && <span className="badge">{t('labels.shipped')}</span>}
          {!template.is_enabled && <span className="badge">{t('labels.hidden')}</span>}
        </div>
        <div className="templates__actions">
          <button type="button" onClick={() => setOpen((current) => !current)}>
            {t('labels.designer.settings')}
          </button>
          {canEdit && !template.is_seeded && (
            <button type="button" onClick={onDesign}>
              {t('labels.designer.open')}
            </button>
          )}
          {canEdit && (
            <button type="button" onClick={onCopy}>
              {t('labels.copy')}
            </button>
          )}
          {canEdit && !template.is_seeded && (
            <button type="button" onClick={onDelete}>
              {t('common.delete')}
            </button>
          )}
        </div>
      </div>
      {open && <TemplateSettings template={template} />}
    </li>
  );
}

/**
 * What the template spends, and where it does not apply.
 *
 * Both are answers about this workshop rather than about the layout, which is
 * why they sit here and not in the designer: they survive a re-seed, and they
 * are set on shipped templates too.
 */
function TemplateSettings({ template }: { template: LabelTemplate }) {
  const { t } = useTranslation();
  const { pick } = useLabel();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const stock = useQuery({
    queryKey: ['labels', 'templates', template.id, 'stock'],
    queryFn: () => templateStock(template.id),
  });

  // Only the types this workshop actually uses. The whole reference book runs
  // to dozens, and a blacklist is read every time somebody wonders why a label
  // is missing from a dropdown.
  const types = useQuery({ queryKey: ['reference', 'types', 'used'], queryFn: () => listTypes('used') });

  const save = useMutation({
    mutationFn: (changes: Parameters<typeof updateTemplate>[1]) =>
      updateTemplate(template.id, changes),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['labels', 'templates'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const excluded = new Set(template.excluded_type_ids);
  const toggle = (id: string) => {
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    save.mutate({ excluded_type_ids: [...next] });
  };

  return (
    <div className="templates__settings">
      {error && (
        <p className="panel__error">{t(`labels.errors.${error}`, t('labels.errors.unknown'))}</p>
      )}

      <section>
        <h4>{t('labels.visibility.title')}</h4>
        <label className="check">
          <input type="checkbox" checked={template.is_enabled} onChange={(event) => save.mutate({ is_enabled: event.target.checked })} />
          {t('labels.visibility.enabled')}
        </label>
        <p className="hint">{t('labels.visibility.hint')}</p>
      </section>

      <section>
        <h4>{t('labels.stock.title')}</h4>
        {stock.data?.component_id ? (
          <>
            <p className="templates__bound">
              <strong>{stock.data.code}</strong> {stock.data.name}
              {stock.data.remaining !== null && (
                <em> · {t('labels.stock.remaining', { n: num(stock.data.remaining) })}</em>
              )}
            </p>
            <LocationPicker
              label={t('labels.stock.place')}
              value={template.stock_location_id}
              componentId={stock.data.component_id}
              onChange={(id) => save.mutate({ stock_location_id: id })}
            />
            <p className="hint">{t('labels.stock.placeHint')}</p>
            <button
              type="button"
              onClick={() => save.mutate({ stock_component_id: null, stock_location_id: null })}
            >
              {t('labels.stock.unbind')}
            </button>
          </>
        ) : (
          <>
            <ComponentPicker
              label={t('labels.stock.pick')}
              value={null}
              onChange={(component) =>
                component && save.mutate({ stock_component_id: component.id })
              }
            />
            <p className="hint">{t('labels.stock.hint')}</p>
          </>
        )}
      </section>

      <section>
        <h4>{t('labels.scope.title')}</h4>
        <p className="hint">{t('labels.scope.hint')}</p>
        <ul className="templates__types">
          {(types.data ?? []).map((type) => (
            <li key={type.id}>
              <label>
                <input
                  type="checkbox"
                  checked={excluded.has(type.id)}
                  onChange={() => toggle(type.id)}
                />
                {pick(type.name_ru, type.name_en)}
              </label>
            </li>
          ))}
        </ul>
        {(types.data ?? []).length === 0 && <p className="panel__hint">{t('labels.scope.noTypes')}</p>}
      </section>
    </div>
  );
}
